import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";

export function Toolbar({ className, ...props }: React.ComponentProps<"div">) {
	return (
		<div
			role="toolbar"
			className={cn("relative flex select-none items-center", className)}
			{...props}
		/>
	);
}

export const toolbarButtonVariants = cva(
	"inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium text-sm outline-none transition-[color,box-shadow] hover:bg-muted hover:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-pressed:bg-accent aria-pressed:text-accent-foreground [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0",
	{
		defaultVariants: {
			size: "sm",
			variant: "default",
		},
		variants: {
			size: {
				default: "h-9 min-w-9 px-2",
				lg: "h-10 min-w-10 px-2.5",
				sm: "h-8 min-w-8 px-1.5",
			},
			variant: {
				default: "bg-transparent",
				outline:
					"border border-input bg-transparent shadow-xs hover:bg-accent hover:text-accent-foreground",
			},
		},
	},
);

type ToolbarButtonProps = {
	pressed?: boolean;
	isDropdown?: boolean;
	tooltip?: string;
} & React.ButtonHTMLAttributes<HTMLButtonElement> &
	VariantProps<typeof toolbarButtonVariants>;

export const ToolbarButton = React.forwardRef<HTMLButtonElement, ToolbarButtonProps>(
	function ToolbarButton(
		{ children, className, isDropdown, pressed, size = "sm", variant, tooltip, ...props },
		ref,
	) {
		return (
			<button
				ref={ref}
				type="button"
				aria-pressed={pressed}
				title={tooltip}
				className={cn(toolbarButtonVariants({ size, variant }), isDropdown && "pr-1", className)}
				{...props}
			>
				{children}
			</button>
		);
	},
);

export function ToolbarGroup({ children, className }: React.ComponentProps<"div">) {
	return (
		<div className={cn("group/toolbar-group", "relative hidden has-[button]:flex", className)}>
			<div className="flex items-center">{children}</div>
			<div className="group-last/toolbar-group:hidden! mx-1.5 py-0.5">
				<Separator orientation="vertical" />
			</div>
		</div>
	);
}

export function ToolbarSeparator({ className }: { className?: string }) {
	return <div className={cn("mx-2 my-1 w-px shrink-0 bg-border", className)} />;
}
